import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

/**
 * TabNav: Tab navigation component.
 *
 * variant="default": left-aligned content-width tabs, orange active text + underline (FAQ, MyPage, TourDetail)
 * variant="fullwidth": equal-width centered tabs, dark text, orange bottom indicator,
 *                       full-width white bar with internal max-w-content (Reservation pages)
 *                       Search bar is rendered by the parent page inside content area.
 */
export default function TabNav({
  tabs, activeTab, onTabChange, className = '', variant = 'default',
  searchValue, onSearch, searchPlaceholder,
}) {
  const { t } = useTranslation();
  if (variant === 'fullwidth') {
    return (
      <div className="bg-white border-t border-mono-100">
        <div className="max-w-content mx-auto flex">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`flex-1 h-12 text-sm font-medium text-mono-900 flex items-center justify-center relative transition ${
                  !isActive ? 'hover:bg-mono-50' : ''
                }`}
              >
                {tab.labelKey ? t(tab.labelKey) : tab.label}
                {isActive && <span className="absolute bottom-0 left-0 right-0 h-px bg-orange-500" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  /* Admin variant — 모바일 최적화: 좁은 화면에선 padding/font 축소 + 우측 페이드로 스크롤 가능 affordance */
  if (variant === 'admin') {
    return (
      <div className={`relative ${className}`}>
        <div
          className="flex border-b border-mono-100 overflow-x-auto scrollbar-hide gap-0.5 sm:gap-1"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`px-2.5 sm:px-4 h-10 sm:h-11 text-2sm sm:text-sm font-medium whitespace-nowrap transition relative flex items-center shrink-0 ${
                activeTab === tab.id
                  ? 'text-orange-500'
                  : 'text-mono-500 hover:text-mono-900'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />
              )}
            </button>
          ))}
        </div>
        {/* 모바일에서 스크롤 가능한 affordance — 우측 페이드 */}
        <div className="sm:hidden absolute top-0 right-0 bottom-0 w-6 pointer-events-none bg-gradient-to-l from-mono-50 to-transparent" />
      </div>
    );
  }

  /* Default variant */
  return (
    <div className={`bg-white ${className}`}>
      <div className="max-w-container mx-auto px-5 flex border-b border-mono-100 overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`px-5 h-12 text-sm font-medium whitespace-nowrap transition relative flex items-center ${
              activeTab === tab.id
                ? 'text-orange-500'
                : 'text-mono-500 hover:text-mono-900'
            }`}
          >
            {tab.labelKey ? t(tab.labelKey) : tab.label}
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />
            )}
          </button>
        ))}
      </div>
      {onSearch && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 h-10 px-3 bg-mono-50 rounded-lg">
            <Icon name="search" size={18} className="text-mono-400 shrink-0" />
            <input
              type="text"
              placeholder={searchPlaceholder || t('common.search')}
              value={searchValue || ''}
              onChange={(e) => onSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm text-mono-900 placeholder:text-mono-400 outline-none"
            />
          </div>
        </div>
      )}
    </div>
  );
}
